export interface ProductGroup {
  title: string;
  description: string;
  scope: string[];
  usage?: string;
  buyerValue?: string;
  image?: string;
}

export interface ProductCategory {
  title: string;
  description: string;
  image: string;
  link: string;
}

export const PRODUCT_CATEGORIES: ProductCategory[] = [
  {
    title: "CNC Laser Cut Components",
    description: "Precision flat steel components cut to exact specifications.",
    image: "/images/cnc-laser-cutting.jpg",
    link: "/cnc",
  },
  {
    title: "Press Brake Formed Components",
    description: "Bent sheet metal shapes for structural support applications.",
    image: "/images/press-brake-bending.jpg",
    link: "/cnc",
  },
  {
    title: "Heavy Fabrication & Structural Assemblies",
    description: "Large welded structures including skids, frames, and platforms.",
    image: "/images/steel-fabrication.jpg",
    link: "/steel",
  },
  {
    title: "Tube & Pipe Fabrication",
    description: "Poles, stands, frames, arms, rails, and barriers from tubular steel.",
    image: "/images/tube-fabrication.jpg",
    link: "/cnc",
  },
  {
    title: "Electrical & Instrumentation Supports",
    description: "Junction box supports, panel stands, and cable mounting hardware.",
    image: "/images/electrical-supply.jpg",
    link: "/steel",
  },
  {
    title: "Stainless Steel Fabrication",
    description: "Corrosion-resistant components for demanding environments.",
    image: "/images/products/stainless-fabrication.jpg",
    link: "/steel",
  },
  {
    title: "Standard Parts Library",
    description: "Common fabrication items in various sizes and finishes.",
    image: "/images/workshop.jpg",
    link: "/cnc",
  },
  {
    title: "Custom Project Manufacturing",
    description: "Drawing-based and BOQ-based custom fabrication solutions.",
    image: "/images/project-support.jpg",
    link: "/steel",
  },
];

export const PRODUCT_GROUPS: ProductGroup[] = [
  {
    title: "Industrial Trading",
    description:
      "Sky Mark Co. supplies industrial materials and project-related products used in infrastructure, construction, utility, and industrial environments.",
    scope: [
      "Industrial components",
      "Project accessories",
      "Installation materials",
      "Support items",
      "General project supply",
      "Sourcing support for urgent project requirements",
    ],
    buyerValue:
      "One contact point for multiple material requirements. Practical support for urgent project needs. Suitable for BOQ-based inquiries. Direct coordination with responsible person.",
  },
  {
    title: "Electrical Supply",
    description:
      "Electrical supply support for construction, infrastructure, and industrial project requirements.",
    scope: [
      "Electrical accessories",
      "Installation materials",
      "Support items",
      "Cable management accessories",
      "Equipment support items",
      "Project-related electrical components",
      "Electrical material packages",
    ],
    usage:
      "Building projects, infrastructure utilities, industrial installations, contractor material requirements, site installation support.",
  },
  {
    title: "Mechanical Supply",
    description:
      "Mechanical material support for project teams requiring mechanical accessories, supports, fabricated items, and related supply requirements.",
    scope: [
      "Mechanical support items",
      "Fabricated accessories",
      "Mounting parts",
      "Steel supports",
      "Pipe support items",
      "Project material packages",
      "Industrial mechanical accessories",
    ],
  },
  {
    title: "Civil Materials Supply",
    description:
      "Civil material support for construction and infrastructure projects.",
    scope: [
      "Civil project materials",
      "Construction support items",
      "Infrastructure support materials",
      "Installation support products",
      "Project-based material coordination",
    ],
  },
  {
    title: "CNC Laser Cut Components",
    description:
      "Flat metal parts cut from sheet material using CNC laser cutting.",
    scope: [
      "Flat mounting plates",
      "Instrument mounting plates",
      "Panel back plates",
      "Equipment mounting plates",
      "Base plates",
      "Anchor plates",
      "Embedded plates",
      "Adapter plates",
      "Spacer plates",
      "Shim plates",
      "Gussets",
      "Stiffeners",
      "Slotted rails",
      "Clamp strips",
      "Cover plates",
      "Guard plates",
      "Custom profiles",
    ],
    usage:
      "Equipment installation, electrical supports, instrumentation supports, structural frames, project-specific fabricated assemblies.",
    image: "/images/cnc-laser-cutting.jpg",
  },
  {
    title: "Press Brake Formed Components",
    description:
      "Bent sheet metal products used as supports, covers, frames, and structural accessories.",
    scope: [
      "L brackets",
      "U brackets",
      "Z brackets",
      "Angle brackets",
      "Reinforced brackets",
      "Cantilever brackets",
      "U channels",
      "C channels",
      "Hat channels",
      "Z profiles",
      "Formed covers",
      "Rain hoods",
      "Drip shields",
      "Small enclosures",
      "Folded support sections",
    ],
    usage:
      "Cable route supports, pipe supports, equipment mounting, field installation, fabricated assemblies, infrastructure supports.",
    image: "/images/press-brake-bending.jpg",
  },
  {
    title: "Heavy Fabrication & Structural Assemblies",
    description:
      "Larger welded steel items and assemblies used in projects.",
    scope: [
      "Equipment skids",
      "Pump skids",
      "Instrument skids",
      "Panel skids",
      "Support frames",
      "Structural frames",
      "Platforms",
      "Walkways",
      "Stairs",
      "Ladders",
      "Racks",
      "Storage structures",
      "Custom welded assemblies",
    ],
    usage:
      "Industrial plants, infrastructure facilities, equipment support areas, utility sites, project installation zones.",
    image: "/images/steel-fabrication.jpg",
  },
  {
    title: "Tube & Pipe Fabricated Items",
    description:
      "Fabricated products made from round tubes, square tubes, or pipe sections.",
    scope: [
      "Instrument stands",
      "Single-post stands",
      "Double-post stands",
      "Lighting poles",
      "CCTV poles",
      "Equipment poles",
      "Wall arms",
      "Light fixture arms",
      "Camera arms",
      "Handrails",
      "Guardrails",
      "Bollards",
      "Safety barriers",
      "Tube frames",
    ],
    usage:
      "Infrastructure sites, utilities, industrial plants, outdoor installations, safety and access areas.",
    image: "/images/tube-fabrication.jpg",
  },
  {
    title: "Electrical & Instrumentation Supports",
    description:
      "Steel support items used to mount electrical and instrumentation equipment.",
    scope: [
      "Junction box supports",
      "Wall-mounted junction box brackets",
      "Post-mounted junction box supports",
      "Double junction box supports",
      "Panel stands",
      "Raised panel supports",
      "Instrument stands",
      "Cable cleat plates",
      "Cable clamp plates",
      "Spacer plates",
      "Accessory mounting plates",
      "Multi-tier support frames",
    ],
    usage:
      "Field instrumentation, electrical installations, control areas, infrastructure utilities, industrial plants.",
    image: "/images/electrical-supply.jpg",
  },
  {
    title: "Stainless Steel Fabrication",
    description:
      "Fabricated stainless steel components for corrosive or demanding environments.",
    scope: [
      "Stainless steel brackets",
      "Stainless mounting plates",
      "Stainless covers",
      "Stainless frames",
      "Stainless stands",
      "Stainless supports",
      "Stainless custom assemblies",
    ],
    usage:
      "Water projects, desalination plants, outdoor corrosive areas, humid environments, chemical or industrial locations.",
    image: "/images/products/stainless-fabrication.jpg",
  },
  {
    title: "Coated Fabricated Components",
    description:
      "Fabricated items finished with powder coating, epoxy coating, paint, or other required finishes.",
    scope: [
      "Coated brackets",
      "Coated supports",
      "Coated frames",
      "Coated covers",
      "Coated stands",
      "Coated custom items",
      "Epoxy-coated industrial components",
    ],
    buyerValue:
      "Coating improves appearance, protection, durability, and suitability for project environments.",
  },
  {
    title: "Custom Project Manufacturing",
    description:
      "Custom items manufactured based on drawings, sketches, BOQs, material lists, or project-specific requirements.",
    scope: [
      "Drawing-based fabrication",
      "BOQ-based material packages",
      "Custom brackets",
      "Custom supports",
      "Special frames",
      "Modified plates",
      "Project-specific assemblies",
      "Batch production",
      "Small custom runs",
    ],
    buyerValue:
      "This is useful when standard catalog items do not match the exact project requirement.",
  },
];

export const CNC_PRODUCT_FAMILIES = [
  {
    title: "Mounting Plates",
    description:
      "Flat plates with holes used to mount equipment, junction boxes, instruments, panels, and accessories.",
    variations: [
      "Rectangular mounting plates",
      "Square mounting plates",
      "Slotted mounting plates",
      "Multi-hole mounting plates",
      "Instrument plates",
      "Panel back plates",
      "Equipment plates",
    ],
  },
  {
    title: "Base Plates & Anchor Plates",
    description:
      "Thicker steel plates used at the bottom of poles, frames, stands, or structures.",
    variations: [
      "Square base plates",
      "Rectangular base plates",
      "Slotted anchor plates",
      "Pole base plates",
      "Grouted base plates",
      "Embedded plates",
      "Bolt templates",
    ],
  },
  {
    title: "Adapter Plates, Spacer Plates & Shims",
    description:
      "Flat plates used to adjust alignment, match different hole patterns, or create spacing.",
    variations: [
      "Adapter plates",
      "Spacer plates",
      "Shim plates",
      "Leveling plates",
      "Conversion plates",
      "Custom hole pattern plates",
    ],
  },
  {
    title: "Gussets & Stiffeners",
    description:
      "Triangular or shaped reinforcement plates used to strengthen brackets, frames, skids, and welded joints.",
    variations: [
      "Triangular gussets",
      "Corner gussets",
      "Rib stiffeners",
      "Frame stiffeners",
      "Channel stiffeners",
      "Reinforcement tabs",
    ],
  },
  {
    title: "Rails, Strips & Clamp Bars",
    description:
      "Long narrow flat parts with holes or slots used for fixing, clamping, and adjustable installation.",
    variations: [
      "Slotted rails",
      "Mounting strips",
      "Cable clamp bars",
      "Cleat plates",
      "Support strips",
      "Multi-hole strips",
    ],
  },
  {
    title: "Covers & Guards",
    description:
      "Flat or lightly formed protective parts used to cover openings, protect equipment, or shield sensitive areas.",
    variations: [
      "Cover plates",
      "Guard plates",
      "Blanking plates",
      "Access panels",
      "Protective shields",
      "Machine guards",
    ],
  },
];

export const PRESS_BRAKE_FAMILIES = [
  {
    title: "L, U, Z Brackets",
    description:
      "Bent steel supports used to hold, fix, or carry equipment, trays, pipes, and accessories.",
    variations: [
      "L brackets",
      "U brackets",
      "Z brackets",
      "Angle brackets",
      "Slotted brackets",
      "Adjustable brackets",
      "Reinforced brackets",
    ],
  },
  {
    title: "Cantilever Brackets",
    description:
      "Wall-mounted extended supports designed to carry loads away from a wall or vertical support.",
    variations: [
      "Short cantilever brackets",
      "Medium cantilever brackets",
      "Heavy-duty cantilever brackets",
      "Gusseted cantilever brackets",
      "Tray support cantilevers",
      "Pipe support cantilevers",
    ],
  },
  {
    title: "Channels & Profiles",
    description:
      "Long formed steel sections used for framing, support, reinforcement, and mounting.",
    variations: [
      "U channels",
      "C channels",
      "Z profiles",
      "Hat channels",
      "Custom folded profiles",
      "Support channels",
    ],
  },
  {
    title: "Covers, Hoods & Enclosures",
    description:
      "Bent sheet metal parts used to protect equipment from impact, dust, rain, or site conditions.",
    variations: [
      "Rain hoods",
      "Drip shields",
      "Equipment covers",
      "Cable protection covers",
      "Small fabricated enclosures",
      "Inspection covers",
    ],
  },
];

export const TUBE_FABRICATION_FAMILIES = [
  {
    title: "Instrument Stands",
    description:
      "Vertical support stands used to mount gauges, transmitters, sensors, and field instruments.",
    variations: [
      "Single-post instrument stands",
      "Double-post instrument stands",
      "Stands with top plate",
      "Stands with side plate",
      "Stands with cable support",
      "Heavy-duty instrument stands",
    ],
  },
  {
    title: "Lighting Poles",
    description:
      "Steel poles used for outdoor lighting and industrial illumination.",
    variations: [
      "Short lighting poles",
      "Medium-height poles",
      "Poles with single arm",
      "Poles with double arm",
      "Custom pole bases",
      "Pole base plates",
    ],
  },
  {
    title: "CCTV & Equipment Poles",
    description:
      "Poles used to mount cameras, monitoring devices, communication items, or outdoor equipment.",
    variations: [
      "CCTV poles",
      "Camera mounting poles",
      "Poles with arms",
      "Equipment support poles",
      "Reinforced outdoor poles",
    ],
  },
  {
    title: "Wall Arms & Project Support Arms",
    description:
      "Horizontal arms fixed to walls, poles, or structures to support equipment.",
    variations: [
      "Straight wall arms",
      "Angled arms",
      "Light fixture arms",
      "Camera arms",
      "Gusseted arms",
      "Long projection arms",
    ],
  },
  {
    title: "Handrails, Bollards & Barriers",
    description:
      "Safety and protection products fabricated from tubes or pipes.",
    variations: [
      "Handrails",
      "Guardrails",
      "Bollards",
      "Protective barriers",
      "Safety posts",
      "Perimeter barriers",
    ],
  },
];

export const STEEL_FABRICATION_SECTIONS = [
  {
    title: "Equipment Skids",
    description:
      "Steel base frames used to hold pumps, panels, mechanical units, instruments, or other equipment.",
    variations: [
      "Pump skids",
      "Panel skids",
      "Equipment skids",
      "Instrument skids",
      "Skid base frames",
      "Skids with lifting lugs",
      "Skids with forklift pockets",
      "Skids with mounting plates",
    ],
  },
  {
    title: "Structural Frames",
    description:
      "Welded steel frames used to support equipment, create mounting structures, or form project-specific assemblies.",
    variations: [
      "Support frames",
      "Equipment frames",
      "Rack frames",
      "Base frames",
      "Multi-level frames",
      "Reinforced frames",
      "Custom welded frames",
    ],
  },
  {
    title: "Platforms & Walkways",
    description:
      "Fabricated access structures used for maintenance, movement, or elevated equipment access.",
    variations: [
      "Access platforms",
      "Maintenance platforms",
      "Walkways",
      "Raised platforms",
      "Platforms with handrails",
      "Platforms with stairs",
      "Service platforms",
    ],
  },
  {
    title: "Stairs & Ladders",
    description:
      "Steel access systems used to reach elevated areas, platforms, equipment, rooftops, or service points.",
    variations: [
      "Straight ladders",
      "Fixed ladders",
      "Inclined stairs",
      "Access stairs",
      "Ladder frames",
      "Stair frames",
      "Maintenance ladders",
    ],
  },
  {
    title: "Racks, Stands & Storage Structures",
    description:
      "Fabricated steel structures used for holding, supporting, storing, or organizing materials and equipment.",
    variations: [
      "Storage racks",
      "Material racks",
      "Tool racks",
      "Drum stands",
      "Panel stands",
      "Equipment stands",
      "Support stands",
    ],
  },
];

export const ELECTRICAL_SUPPORTS = [
  {
    title: "Junction Box Supports",
    description:
      "Support structures used to mount junction boxes at the required height and position.",
    variations: [
      "Wall-mounted junction box brackets",
      "Post-mounted supports",
      "Double-box supports",
      "Large mounting plate supports",
      "Adjustable junction box supports",
      "Reinforced junction box frames",
    ],
  },
  {
    title: "Panel Stands",
    description:
      "Fabricated frames or bases used to support electrical panels, control panels, or equipment cabinets.",
    variations: [
      "Floor-mounted panel stands",
      "Raised panel supports",
      "Reinforced panel frames",
      "Single panel stands",
      "Grouped panel stands",
      "Custom panel support frames",
    ],
  },
  {
    title: "Cable Cleat Plates & Clamp Plates",
    description:
      "Flat or formed steel parts used to mount cable cleats, clamps, and accessories.",
    variations: [
      "Cleat mounting plates",
      "Clamp bars",
      "Support strips",
      "Spacer plates",
      "Backing plates",
      "Cable accessory plates",
    ],
  },
  {
    title: "Multi-Tier Support Frames",
    description:
      "Fabricated frames with multiple mounting levels for equipment, boxes, cables, or accessories.",
    variations: [
      "Two-level frames",
      "Multi-box support frames",
      "Combined electrical support frames",
      "Custom utility support frames",
    ],
  },
];

export const STANDARD_PARTS_LIBRARY = [
  {
    title: "Brackets Family",
    items: [
      "L brackets",
      "U brackets",
      "Z brackets",
      "Angle brackets",
      "Slotted brackets",
      "Adjustable brackets",
      "Reinforced brackets",
      "Cantilever brackets",
      "Gusseted brackets",
      "Tray support brackets",
      "Pipe support brackets",
      "Wall mounting brackets",
      "Ceiling brackets",
      "Suspension brackets",
      "Panel brackets",
    ],
  },
  {
    title: "Plates Family",
    items: [
      "Mounting plates",
      "Base plates",
      "Anchor plates",
      "Adapter plates",
      "Spacer plates",
      "Shim plates",
      "Embedded plates",
      "Cover plates",
      "Blank plates",
      "Equipment plates",
      "Instrument plates",
      "Panel back plates",
      "Bolt templates",
      "Reinforcement plates",
      "Gusset plates",
    ],
  },
  {
    title: "Rails & Strips Family",
    items: [
      "Slotted rails",
      "Mounting strips",
      "Support strips",
      "Clamp bars",
      "Cable cleat rails",
      "Multi-hole strips",
      "Flat support rails",
      "Channel strips",
      "Accessory rails",
    ],
  },
  {
    title: "Supports & Stands Family",
    items: [
      "Instrument stands",
      "Panel stands",
      "Equipment stands",
      "Junction box stands",
      "Pipe support stands",
      "Lighting pole bases",
      "Camera pole supports",
      "Wall arms",
      "Support frames",
      "Multi-tier frames",
    ],
  },
  {
    title: "Covers & Guards Family",
    items: [
      "Rain hoods",
      "Drip shields",
      "Equipment covers",
      "Cable protection covers",
      "Access covers",
      "Inspection covers",
      "Guard plates",
      "Protective shields",
      "Machine guards",
      "Small enclosures",
    ],
  },
  {
    title: "Frames & Assemblies Family",
    items: [
      "Support frames",
      "Equipment frames",
      "Skid frames",
      "Rack frames",
      "Storage frames",
      "Platform frames",
      "Custom welded assemblies",
      "Structural support assemblies",
    ],
  },
];

export const CATALOGS = [
  {
    title: "Company Profile",
    description:
      "Overview of Sky Mark Co., services, capabilities, and contact details.",
    filePath: "/downloads/sky-mark-company-profile.pdf",
  },
  {
    title: "Industrial Supply Profile",
    description:
      "Overview of industrial, electrical, mechanical, and civil supply support.",
    filePath: "/downloads/industrial-supply-profile.pdf",
  },
  {
    title: "CNC & Fabrication Catalog",
    description:
      "Laser cutting, bending, tube fabrication, steel fabrication, and product families.",
    filePath: "/downloads/cnc-fabrication-catalog.pdf",
  },
  {
    title: "Steel Fabrication Catalog",
    description:
      "Heavy fabrication, frames, skids, platforms, supports, and custom assemblies.",
    filePath: "/downloads/steel-fabrication-catalog.pdf",
  },
  {
    title: "Powder Coating & Epoxy Coating Profile",
    description:
      "Coating solutions for fabricated steel and industrial components.",
    filePath: "/downloads/coating-solutions-profile.pdf",
  },
  {
    title: "Electrical & Mechanical Supply Overview",
    description:
      "Supply support for electrical and mechanical project requirements.",
    filePath: "/downloads/electrical-mechanical-supply-overview.pdf",
  },
];
